import React, { useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Avatar, IconButton, Tooltip, Snackbar, Alert, useTheme
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  AccountBalanceWallet as Icon, Add as AddIcon, Person, TrendingUp, AttachMoney
} from '@mui/icons-material';

const fetchMensajeros = async () => {
  const res = await fetch('/api/mensajeros', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

export default function BalancePage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedMensajero, setSelectedMensajero] = useState(null);
  const [form, setForm] = useState({ monto: '', nota: '' });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const { data } = useQuery('mensajeros', fetchMensajeros, { refetchInterval: 15000 });
  const mensajeros = (data?.data || []).filter(m => m.tipo === 'mensajero');

  const handleAddCredit = async () => {
    if (!selectedMensajero || !form.monto) return;
    try {
      const res = await fetch('/api/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          mensajero_id: selectedMensajero.id,
          monto: parseFloat(form.monto),
          tipo: 'abono',
          nota: form.nota
        })
      });
      const data = await res.json();
      if (data.success) {
        setSnack({ open: true, msg: `$${form.monto} abonados a ${selectedMensajero.nombre}`, severity: 'success' });
        setOpen(false);
        setForm({ monto: '', nota: '' });
        queryClient.invalidateQueries('mensajeros');
      } else {
        setSnack({ open: true, msg: data.error || 'Error', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: 'Error de conexión', severity: 'error' });
    }
  };

  const totalCredito = mensajeros.reduce((s, m) => s + parseFloat(m.credito || 0), 0);
  const activos = mensajeros.filter(m => m.estado === 'activo').length;

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Balance / Créditos</Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography color="textSecondary" variant="caption">Total Mensajeros</Typography>
            <Typography variant="h5">{mensajeros.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography color="textSecondary" variant="caption">Activos</Typography>
            <Typography variant="h5" color="success.main">{activos}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent sx={{ py: 1.5 }}>
            <Typography color="textSecondary" variant="caption">Crédito Total</Typography>
            <Typography variant="h5" color="primary.main">${totalCredito.toFixed(2)}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Mensajero</strong></TableCell>
              <TableCell><strong>Teléfono</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Vehículo</strong></TableCell>
              <TableCell align="right"><strong>Crédito</strong></TableCell>
              <TableCell align="center"><strong>Acción</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mensajeros.map(m => (
              <TableRow key={m.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.primary.main, fontSize: 12 }}>
                      {m.nombre?.charAt(0) || '?'}
                    </Avatar>
                    {m.nombre}
                  </Box>
                </TableCell>
                <TableCell>{m.telefono || '—'}</TableCell>
                <TableCell>
                  <Chip label={m.estado} size="small"
                    color={m.estado === 'activo' ? 'success' : m.estado === 'ocupado' ? 'warning' : 'default'} />
                </TableCell>
                <TableCell>{m.vehiculo_tipo || '—'}</TableCell>
                <TableCell align="right">
                  <Typography fontWeight={700} color={parseFloat(m.credito || 0) > 0 ? 'success.main' : 'text.secondary'}>
                    ${parseFloat(m.credito || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => { setSelectedMensajero(m); setOpen(true); }}>
                    Abonar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {mensajeros.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center"><Typography color="textSecondary">Sin mensajeros</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Abonar Crédito - {selectedMensajero?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Monto" type="number" fullWidth size="small" value={form.monto}
              onChange={e => setForm(f => ({ ...f, monto: e.target.value }))}
              InputProps={{ startAdornment: <AttachMoney sx={{ mr: 0.5, color: theme.palette.text.secondary }} /> }} />
            <TextField label="Nota (opcional)" fullWidth size="small" value={form.nota}
              onChange={e => setForm(f => ({ ...f, nota: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[10, 20, 50, 100].map(v => (
                <Chip key={v} label={`$${v}`} variant={parseFloat(form.monto) === v ? 'filled' : 'outlined'}
                  color="primary" onClick={() => setForm(f => ({ ...f, monto: v.toString() }))} />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddCredit} disabled={!form.monto || parseFloat(form.monto) <= 0}>
            Abonar ${form.monto || '0'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
